import type {
  TreeDataNode as AntTreeDataNode,
  TreeProps as AntTreeProps,
} from 'antd';
import { Tree as AntTree } from 'antd';
import type { FocusEvent, Key, KeyboardEvent } from 'react';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';

export type TreeDataNode = Omit<AntTreeDataNode, 'children'> & {
  'aria-label'?: string;
  children?: TreeDataNode[];
};
export type TreeProps = Omit<AntTreeProps, 'treeData'> & {
  treeData?: TreeDataNode[];
};

const TREE_ITEM_ID = 'data-suntex-tree-item-id';
const INACTIVE_TAB_INDEX = -1;
const INTERACTIVE_SELECTOR =
  'a[href], button, input, select, textarea, [contenteditable="true"], [role="button"], [role="link"]';
const ARROW_DOWN_KEY_CODE = 40;
const ARROW_KEY_CODES: Partial<Record<string, number>> = {
  ArrowLeft: 37,
  ArrowUp: 38,
  ArrowRight: 39,
  ArrowDown: ARROW_DOWN_KEY_CODE,
};

export function Tree({
  activeKey: controlledActiveKey,
  fieldNames,
  focusable = true,
  onActiveChange,
  onBlur,
  onFocus,
  onKeyDown,
  treeData,
  ...props
}: TreeProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const pendingFocusKeyRef = useRef<Key | null>(null);
  const decoratedTree = useMemo(
    () => decorateTreeData(treeData, fieldNames),
    [fieldNames, treeData],
  );
  const [activeKey, setActiveKey] = useState<Key | null>();
  const mergedActiveKey =
    controlledActiveKey === undefined ? activeKey : controlledActiveKey;
  const resolvedActiveKey =
    mergedActiveKey === undefined ||
    (mergedActiveKey !== null && !decoratedTree.idsByKey.has(mergedActiveKey))
      ? decoratedTree.firstKey
      : mergedActiveKey;
  const tabStopKey = resolvedActiveKey;

  const commitActiveKey = (key: Key) => {
    if (controlledActiveKey === undefined) setActiveKey(key);
    onActiveChange?.(key);
  };

  const handleActiveChange: NonNullable<TreeProps['onActiveChange']> = (
    key,
  ) => {
    pendingFocusKeyRef.current = key;
    commitActiveKey(key);
  };

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    updateTreeTabStops(root, tabStopKey, decoratedTree, focusable);

    getAntKeyboardTarget(root)?.setAttribute('aria-hidden', 'true');

    const pendingKey = pendingFocusKeyRef.current;
    if (pendingKey !== null) {
      pendingFocusKeyRef.current = null;
      if (focusable) focusTreeItem(root, pendingKey, decoratedTree);
    }
  }, [decoratedTree, focusable, tabStopKey]);

  const handleFocusCapture = (event: FocusEvent<HTMLDivElement>) => {
    const treeItem = getTreeItem(event.target);
    const key = treeItem && getTreeItemKey(treeItem, decoratedTree);
    if (key !== null && key !== resolvedActiveKey) commitActiveKey(key);

    if (!rootRef.current?.contains(event.relatedTarget)) onFocus?.(event);
  };

  const handleBlurCapture = (event: FocusEvent<HTMLDivElement>) => {
    if (!rootRef.current?.contains(event.relatedTarget)) onBlur?.(event);
  };

  const handleKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>) => {
    const root = rootRef.current;
    const treeItem = getTreeItem(event.target);
    if (!root || !treeItem) return;

    onKeyDown?.(event);
    if (event.defaultPrevented) return;

    const interactiveTarget = getInteractiveTarget(event.target, treeItem);

    if (event.key === 'Tab') {
      if (
        !event.shiftKey &&
        isLastEnabledControl(treeItem, interactiveTarget)
      ) {
        event.preventDefault();
        forwardKeyToAnt(root, 'ArrowDown', ARROW_DOWN_KEY_CODE);
      }
      return;
    }

    if (interactiveTarget) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      if (!isDisabled(treeItem)) getContentWrapper(treeItem)?.click();
      return;
    }

    const keyCode = ARROW_KEY_CODES[event.key];
    if (keyCode === undefined) return;

    event.preventDefault();
    event.stopPropagation();
    forwardKeyToAnt(root, event.key, keyCode);
  };

  return (
    <div
      ref={rootRef}
      onBlurCapture={handleBlurCapture}
      onFocusCapture={handleFocusCapture}
      onKeyDownCapture={handleKeyDownCapture}
    >
      <AntTree
        {...props}
        activeKey={resolvedActiveKey}
        fieldNames={fieldNames}
        focusable={false}
        onActiveChange={handleActiveChange}
        treeData={decoratedTree.treeData}
      />
    </div>
  );
}

interface DecoratedTree {
  firstKey: Key | null;
  idsByKey: Map<Key, string>;
  keysById: Map<string, Key>;
  treeData: TreeProps['treeData'];
}

function decorateTreeData(
  treeData: TreeProps['treeData'],
  fieldNames: TreeProps['fieldNames'],
): DecoratedTree {
  const childrenField = fieldNames?.children ?? 'children';
  const keyField = fieldNames?.key ?? 'key';
  const idsByKey = new Map<Key, string>();
  const keysById = new Map<string, Key>();
  let firstKey: Key | null = null;
  let id = 0;

  const visit = (nodes: TreeProps['treeData']): TreeProps['treeData'] =>
    nodes?.map((node) => {
      const record = node as unknown as Record<string, unknown>;
      const key = record[keyField] as Key;
      const itemId = `${id++}`;
      const children = record[childrenField] as TreeProps['treeData'];

      if (firstKey === null) firstKey = key;
      idsByKey.set(key, itemId);
      keysById.set(itemId, key);

      return {
        ...node,
        [TREE_ITEM_ID]: itemId,
        ...(children ? { [childrenField]: visit(children) } : {}),
      };
    });

  const decoratedData = visit(treeData);
  return { firstKey, idsByKey, keysById, treeData: decoratedData };
}

function getTreeItems(root: HTMLElement) {
  return Array.from(root.querySelectorAll<HTMLElement>('[role="treeitem"]'));
}

function getTreeItem(target: EventTarget) {
  return target instanceof Element
    ? target.closest<HTMLElement>('[role="treeitem"]')
    : null;
}

function getTreeItemKey(treeItem: HTMLElement, tree: DecoratedTree) {
  const id = treeItem.getAttribute(TREE_ITEM_ID);
  return id === null ? null : (tree.keysById.get(id) ?? null);
}

function focusTreeItem(
  root: HTMLElement,
  key: Key,
  tree: DecoratedTree,
  attempts = 0,
) {
  const id = tree.idsByKey.get(key);
  const treeItem = getTreeItems(root).find(
    (item) => item.getAttribute(TREE_ITEM_ID) === id,
  );
  if (treeItem) {
    updateTreeTabStops(root, key, tree, true);
    treeItem.focus({ preventScroll: true });
  } else if (attempts < 2) {
    requestAnimationFrame(() => focusTreeItem(root, key, tree, attempts + 1));
  }
}

function updateTreeTabStops(
  root: HTMLElement,
  activeKey: Key | null,
  tree: DecoratedTree,
  focusable: boolean,
) {
  getTreeItems(root).forEach((treeItem) => {
    const isActive = focusable && getTreeItemKey(treeItem, tree) === activeKey;
    treeItem.tabIndex = isActive ? 0 : INACTIVE_TAB_INDEX;
    setDescendantTabStops(treeItem, isActive);
  });
}

function setDescendantTabStops(treeItem: HTMLElement, enabled: boolean) {
  treeItem
    .querySelectorAll<HTMLElement>(INTERACTIVE_SELECTOR)
    .forEach((item) => {
      if (enabled) {
        const originalTabIndex = item.dataset.suntexTreeTabIndex;
        if (originalTabIndex === undefined) return;
        if (originalTabIndex === '') item.removeAttribute('tabindex');
        else item.setAttribute('tabindex', originalTabIndex);
        delete item.dataset.suntexTreeTabIndex;
      } else {
        if (item.dataset.suntexTreeTabIndex === undefined) {
          item.dataset.suntexTreeTabIndex = item.getAttribute('tabindex') ?? '';
        }
        item.tabIndex = INACTIVE_TAB_INDEX;
      }
    });
}

function isLastEnabledControl(
  treeItem: HTMLElement,
  interactiveTarget: HTMLElement | null,
) {
  const controls = Array.from(
    treeItem.querySelectorAll<HTMLElement>(INTERACTIVE_SELECTOR),
  ).filter((item) => !item.matches(':disabled') && item.tabIndex >= 0);
  return (
    interactiveTarget !== null &&
    interactiveTarget === controls[controls.length - 1]
  );
}

function getInteractiveTarget(target: EventTarget, treeItem: HTMLElement) {
  if (!(target instanceof Element)) return null;
  const interactive = target.closest<HTMLElement>(INTERACTIVE_SELECTOR);
  return interactive && treeItem.contains(interactive) ? interactive : null;
}

function getAntKeyboardTarget(root: HTMLElement) {
  return root.querySelector<HTMLInputElement>(
    'input[aria-label="for screen reader"]',
  );
}

function forwardKeyToAnt(root: HTMLElement, key: string, keyCode: number) {
  const target = getAntKeyboardTarget(root);
  if (!target) return;

  const event = new globalThis.KeyboardEvent('keydown', {
    bubbles: true,
    cancelable: true,
    key,
  });
  Object.defineProperties(event, {
    keyCode: { value: keyCode },
    which: { value: keyCode },
  });
  target.dispatchEvent(event);
}

function getContentWrapper(treeNode: HTMLElement) {
  return Array.from(treeNode.querySelectorAll<HTMLElement>('*')).find((node) =>
    hasClassEndingWith(node, '-node-content-wrapper'),
  );
}

function isDisabled(treeNode: HTMLElement) {
  return hasClassEndingWith(treeNode, '-treenode-disabled');
}

function hasClassEndingWith(element: Element, suffix: string) {
  return Array.from(element.classList).some((className) =>
    className.endsWith(suffix),
  );
}
